import { inject } from "ui-common/di";
import type { IWritableCreatorLiveEventsState, ICreatorLiveEventsService } from "./types";
import type { LiveEvent } from "api-contracts/rpc/get-live-events";
import type { IRpcService } from "ui-common/services/rpc/types";
import type { ISseService } from "ui-common/services/sse/types";
import type { SortDirection } from "ui-components/wow-base-table/types";

interface LiveEventStatusUpdate {
  eventId: string;
  status: string;
  actual_start_time?: string | null;
}

export class CreatorLiveEventsService implements ICreatorLiveEventsService {
  @inject<IWritableCreatorLiveEventsState>("writableCreatorLiveEventsState")
  private state!: IWritableCreatorLiveEventsState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @inject<ISseService>("sseService")
  private sseService!: ISseService;

  private unsubscribeSse: (() => void) | null = null;
  private isLoaded = false;

  async loadEvents(): Promise<void> {
    if (this.isLoaded) return;

    this.state.isLoading.set(true);
    this.state.errorMessage.set("");

    try {
      const result = await this.rpcService.call<{ events: LiveEvent[] }>("get-live-events", {});
      this.state.events.set(result.events);
      this.subscribe();
      this.isLoaded = true;
    } catch (error) {
      this.state.errorMessage.set((error as Error).message);
    } finally {
      this.state.isLoading.set(false);
    }
  }

  async refetch(): Promise<void> {
    if (!this.isLoaded) return;

    try {
      const result = await this.rpcService.call<{ events: LiveEvent[] }>("get-live-events", {});
      this.state.events.set(result.events);
    } catch (error) {
      console.error("[CreatorLiveEvents] Refetch failed:", error);
    }
  }

  subscribe(): void {
    if (this.unsubscribeSse) return;

    const unsubStatus = this.sseService.on<LiveEventStatusUpdate>("live-event-status-change", (data) =>
      this.handleStatusUpdate(data),
    );

    const unsubCreated = this.sseService.on("live-event-created", () => this.refetch());
    const unsubUpdated = this.sseService.on("live-event-updated", () => this.refetch());
    const unsubDeleted = this.sseService.on("live-event-deleted", () => this.refetch());

    this.unsubscribeSse = () => {
      unsubStatus();
      unsubCreated();
      unsubUpdated();
      unsubDeleted();
    };
  }

  unsubscribe(): void {
    this.unsubscribeSse?.();
    this.unsubscribeSse = null;
  }

  initialize(): void {
    this.loadEvents();
  }

  destroy(): void {
    this.unsubscribe();
  }

  clearCache(): void {
    this.unsubscribe();
    this.state.events.set([]);
    this.state.errorMessage.set("");
    this.state.activeSorts.set([]);
    this.isLoaded = false;
  }

  updateSort(column: string, direction: SortDirection): void {
    if (direction === "none") {
      this.state.activeSorts.set(this.state.activeSorts.get().filter((s) => s.column !== column));
    } else {
      const current = this.state.activeSorts.get();
      const existing = current.findIndex((s) => s.column === column);
      if (existing >= 0) {
        const updated = [...current];
        updated[existing] = { column, direction };
        this.state.activeSorts.set(updated);
      } else {
        this.state.activeSorts.set([...current, { column, direction }]);
      }
    }
  }

  updateEventLocally(eventId: string, partial: Partial<LiveEvent>): void {
    const events = this.state.events.get();
    const index = events.findIndex((e) => e.id === eventId);
    if (index === -1) return;

    const updated = [...events];
    updated[index] = { ...events[index]!, ...partial };
    this.state.events.set(updated);
  }

  private handleStatusUpdate(data: LiveEventStatusUpdate): void {
    const events = this.state.events.get();
    const index = events.findIndex((e) => e.id === data.eventId);
    if (index === -1) return;

    const event = events[index]!;
    if (event.status === data.status) return;

    const updated = [...events];
    updated[index] = {
      ...event,
      status: data.status,
      ...(data.actual_start_time != null ? { actual_start_time: data.actual_start_time } : {}),
    };
    this.state.events.set(updated);
  }
}
